import { useDroppable } from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CanvasElement } from "@/components/designer/CanvasElement";
import { SECTION_DEFS } from "@/lib/elements";

/**
 * SectionDropZone — droppable region for one of the 5 invoice sections
 * (reportHeader / pageHeader / body / pageFooter / reportFooter).
 *
 * Owns the section header pill (colored dot + label + description) and the
 * SortableContext that lets dragged elements reorder within the section.
 */
export function SectionDropZone({
  sectionKey,
  elements,
  selectedIds,
  onSelectElement,
  onDeleteElement,
  onDuplicateElement,
}) {
  const def = SECTION_DEFS[sectionKey];
  const { setNodeRef, isOver } = useDroppable({ id: `section-${sectionKey}` });
  const isEmpty = elements.length === 0;
  const isBody = sectionKey === "body";

  return (
    <div className="relative mb-3" data-testid={`section-${sectionKey}`}>
      <div className="flex items-center gap-1.5 mb-1 px-1">
        <div
          className="w-1.5 h-1.5 rounded-full"
          style={{ backgroundColor: def.color }}
        />
        <span
          className="text-[9px] font-bold uppercase tracking-wider"
          style={{ color: def.color }}
        >
          {def.label}
        </span>
        <span className="text-[9px] text-gray-300">{def.description}</span>
      </div>

      <div
        ref={setNodeRef}
        className={`rounded transition-all duration-200 ${
          isOver
            ? "bg-blue-50/40 ring-1 ring-blue-300"
            : isEmpty
            ? "bg-gray-50/30"
            : ""
        }`}
        style={{
          minHeight: isBody ? 80 : 36,
          borderLeft: `2px solid ${def.color}`,
          paddingLeft: 8,
        }}
      >
        <SortableContext
          id={sectionKey}
          items={elements.map((el) => el.id)}
          strategy={verticalListSortingStrategy}
        >
          {elements.map((el) => (
            <CanvasElement
              key={el.id}
              element={el}
              isSelected={selectedIds.includes(el.id)}
              onClick={(e) => onSelectElement(el.id, e?.shiftKey)}
              onDelete={() => onDeleteElement(el.id)}
              onDuplicate={() => onDuplicateElement(el.id)}
            />
          ))}
        </SortableContext>

        {isEmpty && (
          <div className="flex items-center justify-center py-2">
            <p className="text-[9px] text-gray-300">Drop elements here</p>
          </div>
        )}
      </div>
    </div>
  );
}
